import { useSavedColorPreferences } from "@/api/hooks/account";
import OpenInNewIcon from "@/assets/icons/OpenInNew.module.svg";
import PipetteIcon from "@/assets/icons/Pipette.module.svg";
import { Button } from "@/components/atoms/Button/Button";
import { t } from "@lingui/core/macro";
import { type ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { ColorPicker, ColorService, type IColor } from "react-color-palette";
import "./colorpicker-override.css";
import { SAVED_COLOR_SLOT_IDS, colorToHex8 } from "./savedColors";

function rgbChannelToHex(value: number) {
  return Math.round(value).toString(16).padStart(2, "0");
}

function toNativeColorInputHex(color: IColor) {
  return `#${rgbChannelToHex(color.rgb.r)}${rgbChannelToHex(color.rgb.g)}${rgbChannelToHex(color.rgb.b)}`;
}

export type GenericColorPickerSavedColorsState = {
  hasUnsavedChanges: boolean;
  isEditMode: boolean;
  saveDraft: () => Promise<void>;
  discardDraft: () => void;
};

export function GenericColorPicker(props: {
  color: IColor;
  onColorChange: (color: IColor) => void;
  closeOnSavedColorSelect?: boolean;
  onRequestClose?: () => void;
  onSavedColorsStateChange?: (state: GenericColorPickerSavedColorsState | null) => void;
}) {
  const { closeOnSavedColorSelect, onColorChange, onRequestClose, onSavedColorsStateChange } = props;
  const [isPickingColor, setIsPickingColor] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const { savedColors, saveSavedColors, isSaving } = useSavedColorPreferences();
  const [draftSavedColors, setDraftSavedColors] = useState(savedColors);
  const [activeSavedColorIndex, setActiveSavedColorIndex] = useState(0);
  const nativeColorInputRef = useRef<HTMLInputElement>(null);
  const eyeDropperSupported = typeof window !== "undefined" && "EyeDropper" in window;
  const normalizedCurrentColor = colorToHex8(props.color);
  const savedColorsInUse = isEditMode ? draftSavedColors : savedColors;
  const hasUnsavedChanges = draftSavedColors.some((color, index) => color !== savedColors[index]);
  const pickerColor = useMemo(() => {
    if (!isEditMode) {
      return props.color;
    }

    return ColorService.convert("hex", draftSavedColors[activeSavedColorIndex]);
  }, [activeSavedColorIndex, draftSavedColors, isEditMode, props.color]);

  useEffect(() => {
    if (!isEditMode || !hasUnsavedChanges) {
      setDraftSavedColors(savedColors);
    }
  }, [hasUnsavedChanges, isEditMode, savedColors]);

  useEffect(() => {
    onSavedColorsStateChange?.({
      hasUnsavedChanges,
      isEditMode,
      saveDraft: async () => {
        await saveSavedColors(draftSavedColors);
        setIsEditMode(false);
      },
      discardDraft: () => {
        setDraftSavedColors(savedColors);
        setIsEditMode(false);
      },
    });

    return () => {
      onSavedColorsStateChange?.(null);
    };
  }, [draftSavedColors, hasUnsavedChanges, isEditMode, onSavedColorsStateChange, saveSavedColors, savedColors]);

  const applyColorAndPreserveAlpha = (hex: string) => {
    const pickedColor = ColorService.convert("hex", hex);
    const alphaSource = isEditMode ? pickerColor : props.color;
    const colorWithOriginalAlpha = ColorService.convert("rgb", {
      ...pickedColor.rgb,
      a: alphaSource.rgb.a,
    });
    handlePickerColorChange(colorWithOriginalAlpha);
  };

  const openNativeColorPicker = () => {
    nativeColorInputRef.current?.click();
  };

  const handleEyeDropperClick = async () => {
    const EyeDropperCtor =
      typeof window !== "undefined"
        ? (
            window as Window & {
              EyeDropper?: new () => {
                open: () => Promise<{ sRGBHex: string }>;
              };
            }
          ).EyeDropper
        : undefined;

    if (!EyeDropperCtor || isPickingColor) {
      openNativeColorPicker();
      return;
    }

    setIsPickingColor(true);
    try {
      const eyeDropper = new EyeDropperCtor();
      const { sRGBHex } = await eyeDropper.open();
      applyColorAndPreserveAlpha(sRGBHex);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }
      openNativeColorPicker();
    } finally {
      setIsPickingColor(false);
    }
  };

  const handleNativeColorChange = (event: ChangeEvent<HTMLInputElement>) => {
    applyColorAndPreserveAlpha(event.target.value);
  };

  const handlePickerColorChange = (nextColor: IColor) => {
    if (isEditMode) {
      setDraftSavedColors((prev) =>
        prev.map((color, index) => (index === activeSavedColorIndex ? colorToHex8(nextColor) : color)),
      );
      return;
    }

    onColorChange(nextColor);
  };

  const handleSavedColorClick = (savedColor: string, index: number) => {
    if (isEditMode) {
      setActiveSavedColorIndex(index);
      return;
    }

    onColorChange(ColorService.convert("hex", savedColor));
    if (closeOnSavedColorSelect) {
      onRequestClose?.();
    }
  };

  const handleEditModeToggle = async () => {
    if (isEditMode) {
      await saveSavedColors(draftSavedColors);
      setIsEditMode(false);
      return;
    }

    setDraftSavedColors(savedColors);
    const selectedIndex = savedColors.findIndex((savedColor) => savedColor === normalizedCurrentColor);
    setActiveSavedColorIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setIsEditMode(true);
  };

  const actionLabel = t`Sample color`;
  const savedColorsTitle = t`Saved colors`;
  const editLabel = t`Edit`;
  const saveLabel = t`Save`;

  return (
    <div data-testid="generic-color-picker" className="generic-color-picker">
      <ColorPicker hideInput={["hsv"]} color={pickerColor} onChange={handlePickerColorChange} />
      <div className="saved-colors">
        <div className="saved-colors__header">
          <div className="saved-colors__title">{savedColorsTitle}</div>
          <Button
            buttonType="clear"
            type="button"
            className="saved-colors__edit-button"
            onClick={() => {
              void handleEditModeToggle();
            }}
            disabled={isSaving}
          >
            {isEditMode ? saveLabel : editLabel}
          </Button>
        </div>
        <div className="saved-colors__grid" data-edit-mode={String(isEditMode)}>
          {savedColorsInUse.map((savedColor, index) => {
            const isSelected = isEditMode ? index === activeSavedColorIndex : savedColor === normalizedCurrentColor;

            return (
              <button
                key={SAVED_COLOR_SLOT_IDS[index]}
                type="button"
                className="saved-colors__swatch-button"
                data-selected={String(isSelected)}
                data-edit-mode={String(isEditMode)}
                aria-label={`${savedColorsTitle} ${index + 1}`}
                onClick={() => handleSavedColorClick(savedColor, index)}
              >
                <span className="saved-colors__swatch-ring">
                  <span className="saved-colors__swatch-color" style={{ background: savedColor }} />
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="rcp-actions">
        <Button
          data-testid="eyedropper-button"
          className="rcp-eyedropper-button"
          buttonType="clear"
          type="button"
          aria-label={actionLabel}
          onClick={() => {
            void handleEyeDropperClick();
          }}
          disabled={isPickingColor}
        >
          <PipetteIcon />
          <span className="rcp-eyedropper-button__label">{actionLabel}</span>
          {!eyeDropperSupported ? <OpenInNewIcon /> : null}
        </Button>
        <input
          ref={nativeColorInputRef}
          data-testid="eyedropper-fallback-input"
          className="rcp-eyedropper-fallback-input"
          type="color"
          value={toNativeColorInputHex(props.color)}
          onChange={handleNativeColorChange}
          aria-label="Choose color"
          tabIndex={-1}
        />
      </div>
    </div>
  );
}
